import Phaser from 'phaser';
import { TILE_SIZE } from '@game/protocol';

/**
 * As duas marcas que o ponteiro deixa no cenário: o tile mirado por um arraste e o destino
 * de uma caminhada.
 *
 * Existem porque os dois gestos novos são **surdos** sem elas. Um arraste que atravessa a
 * tela sem dizer onde vai cair só se descobre errado depois de soltar; e um clique de andar
 * que não acha caminho é indistinguível de um clique que o jogo não registrou — o
 * personagem fica parado nos dois casos, e sem marca nenhuma a leitura é "travou".
 *
 * A profundidade segue a convenção do resto da cena — a linha da grade, `y * 10` — e fica
 * **abaixo** do item de chão (`-5`) e da criatura (`0`): a marca é um destaque do piso, e um
 * retângulo por cima do rato que se quer atacar esconderia justamente o alvo do gesto.
 */

/** Verde de "pode", o mesmo `.drop-target` do inventário; vermelho de "não dá". */
const COLOR_OK = 0x7fd4a0;
const COLOR_DENIED = 0xff6b6b;

const PING_MS = 320;

export class PointerMarks {
  private readonly scene: Phaser.Scene;
  private readonly outline: Phaser.GameObjects.Graphics;

  /** O que já está desenhado, para não redesenhar o mesmo retângulo a cada quadro. */
  private shownX = -1;
  private shownY = -1;
  private shownOk = false;
  private shown = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.outline = scene.add.graphics();
    this.outline.setVisible(false);
  }

  /** Contorna o tile sob o ponteiro. `ok` é "o gesto vale daqui". */
  highlight(x: number, y: number, ok: boolean): void {
    if (this.shown && this.shownX === x && this.shownY === y && this.shownOk === ok) return;

    this.shownX = x;
    this.shownY = y;
    this.shownOk = ok;
    this.shown = true;

    const color = ok ? COLOR_OK : COLOR_DENIED;

    this.outline.clear();
    this.outline.fillStyle(color, 0.14);
    this.outline.fillRect(x * TILE_SIZE, y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
    this.outline.lineStyle(1, color, 0.9);
    // Meio pixel para dentro: uma linha de 1 px centrada na divisa da célula sai metade em
    // cada tile e aparece com 2 px de espessura esmaecida.
    this.outline.strokeRect(
      x * TILE_SIZE + 0.5,
      y * TILE_SIZE + 0.5,
      TILE_SIZE - 1,
      TILE_SIZE - 1,
    );
    this.outline.setDepth(y * 10 - 7);
    this.outline.setVisible(true);
  }

  clearHighlight(): void {
    if (!this.shown) return;
    this.shown = false;
    this.outline.clear();
    this.outline.setVisible(false);
  }

  /**
   * Um anel que abre e apaga no tile clicado.
   *
   * `reachable` falso é a recusa: mesmo desenho em vermelho, e sem crescer. O clique num
   * lugar aonde não se chega precisa responder alguma coisa, e "alguma coisa" aqui é a
   * diferença entre "o jogo me ignorou" e "o jogo disse que não dá".
   */
  ping(x: number, y: number, reachable: boolean): void {
    const ring = this.scene.add.graphics();
    ring.lineStyle(2, reachable ? COLOR_OK : COLOR_DENIED, 0.9);
    ring.strokeCircle(0, 0, TILE_SIZE / 2 - 2);
    ring.setPosition(x * TILE_SIZE + TILE_SIZE / 2, y * TILE_SIZE + TILE_SIZE / 2);
    ring.setDepth(y * 10 - 7);

    this.scene.tweens.add({
      targets: ring,
      alpha: 0,
      scale: reachable ? 1.5 : 0.7,
      duration: PING_MS,
      ease: 'Quad.easeOut',
      onComplete: () => ring.destroy(),
    });
  }

  /** A cena sobrevive à troca de região; o que morre aqui é só a marca pendurada. */
  destroy(): void {
    this.outline.destroy();
  }
}
